import { useRef, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Popover, PopoverAnchor, PopoverContent } from '@/components/ui/Popover';
import { useToast } from '@/components/ui/Toast';
import {
  applyKeywordBidUpdate,
  formatBidUpdateSuccessMessage,
  type EditableKeywordBidTarget,
} from '@/api/keywordBidUpdate';
import { formatCurrencyValue } from '@/api/keywordMetrics';
import type { WorkspaceSuggestedBid } from '@/utils/workspaceKeywordSuggestedBids';
import { cn } from '@/utils/cn';

type EditableKeywordBidCellProps = {
  profileId: string;
  displayValue: string;
  editableTarget: EditableKeywordBidTarget | null;
  /** Amazon suggested bid shown under the box; clicking a value puts it in the box unsaved. */
  suggestion?: WorkspaceSuggestedBid | null;
  keywordLabel?: string;
  onUpdated?: () => void;
  className?: string;
};

const SUGGESTION_LINK_CLASS =
  'rounded-sm text-brand underline underline-offset-2 hover:text-brand-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/30';

export function EditableKeywordBidCell({
  profileId,
  displayValue,
  editableTarget,
  suggestion,
  keywordLabel,
  onUpdated,
  className,
}: EditableKeywordBidCellProps) {
  const { showToast } = useToast();
  const [isOpen, setIsOpen] = useState(false);
  const [draftBid, setDraftBid] = useState('');
  /** Suggested value picked from the links; shown in the box until applied or cancelled. */
  const [pendingBid, setPendingBid] = useState<number | null>(null);
  /** Box + suggested values; clicks in here never dismiss the editor. */
  const cellRef = useRef<HTMLDivElement>(null);

  const mutation = useMutation({
    mutationFn: async (newBid: number) => {
      if (!editableTarget) throw new Error('Bid is not editable');
      await applyKeywordBidUpdate(profileId, editableTarget, newBid);
      return newBid;
    },
    onSuccess: (newBid) => {
      showToast(formatBidUpdateSuccessMessage(newBid), 'success');
      setPendingBid(null);
      setIsOpen(false);
      onUpdated?.();
    },
    onError: (error) => {
      const message = error instanceof Error ? error.message : 'Failed to update bid';
      showToast(`Failed to update bid${keywordLabel ? ` for ${keywordLabel}` : ''}: ${message}`, 'error');
    },
  });

  /** The box always opens (or keeps open) the editor; it never toggles it closed. */
  const openEditor = () => {
    if (!editableTarget) return;
    if (!isOpen) {
      setDraftBid(pendingBid != null ? pendingBid.toFixed(2) : String(editableTarget.bid));
    }
    setIsOpen(true);
  };

  /** Picking a suggested value fills the box and opens Apply / Cancel right away. */
  const pickSuggestion = (value: number) => {
    setPendingBid(value);
    setDraftBid(value.toFixed(2));
    setIsOpen(true);
  };

  /** Closing without Apply (Cancel, Escape, outside click) drops the unsaved suggested value. */
  const handleOpenChange = (open: boolean) => {
    if (!open) setPendingBid(null);
    setIsOpen(open);
  };

  const handleCancel = () => handleOpenChange(false);

  const handleApply = () => {
    const parsed = Number(draftBid);
    if (!Number.isFinite(parsed) || parsed <= 0) {
      showToast('Enter a valid bid greater than 0', 'error');
      return;
    }
    mutation.mutate(parsed);
  };

  if (!editableTarget) {
    return <span className={className}>{displayValue}</span>;
  }

  const suggestedBid = validBid(suggestion?.suggestedBid);
  const suggestedMin = validBid(suggestion?.suggestedBidMin);
  const suggestedMax = validBid(suggestion?.suggestedBidMax);
  const forKeyword = keywordLabel ? ` for ${keywordLabel}` : '';

  const suggestionLink = (value: number, kind: string) => (
    <button
      type="button"
      onClick={() => pickSuggestion(value)}
      className={SUGGESTION_LINK_CLASS}
      aria-label={`Use ${kind} suggested bid ${formatCurrencyValue(value)}${forKeyword}`}
    >
      {formatCurrencyValue(value)}
    </button>
  );

  const box = (
    <button
      type="button"
      onClick={openEditor}
      aria-haspopup="dialog"
      aria-expanded={isOpen}
      className={cn(
        'inline-flex h-7 min-w-[4.75rem] cursor-pointer items-center justify-center rounded-lg border border-border bg-surface px-2 font-mono text-body-sm tabular-nums text-foreground transition-colors hover:border-brand hover:bg-surface-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/30',
        pendingBid != null && 'border-brand bg-brand-subtle text-brand',
        className
      )}
      aria-label={`${keywordLabel ? `Edit bid for ${keywordLabel}` : 'Edit bid'}${
        pendingBid != null ? ` (unsaved suggested bid ${formatCurrencyValue(pendingBid)})` : ''
      }`}
      title={pendingBid != null ? 'Suggested bid not saved yet. Click to apply.' : undefined}
    >
      {pendingBid != null ? formatCurrencyValue(pendingBid) : displayValue}
    </button>
  );

  // Portaled + collision-aware so table scroll containers and later rows never clip the actions.
  const editorContent = (
    <PopoverContent
      align="end"
      collisionPadding={8}
      className="w-44 p-3 shadow-lg"
      onInteractOutside={(event) => {
        if (cellRef.current?.contains(event.target as Node)) event.preventDefault();
      }}
    >
      <Input
        type="number"
        min="0"
        step="0.01"
        value={draftBid}
        onChange={(event) => setDraftBid(event.target.value)}
        className="mb-2 h-7 px-2 py-1 text-right font-mono text-sm"
        autoFocus
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            event.preventDefault();
            handleApply();
          }
        }}
      />
      <div className="flex items-center justify-end gap-2">
        <Button
          type="button"
          variant="secondary"
          size="sm"
          onClick={handleCancel}
          disabled={mutation.isPending}
        >
          Cancel
        </Button>
        <Button type="button" size="sm" onClick={handleApply} disabled={mutation.isPending}>
          {mutation.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : 'Apply'}
        </Button>
      </div>
    </PopoverContent>
  );

  return (
    <Popover open={isOpen} onOpenChange={handleOpenChange}>
      {/* Anchored to the whole cell so the editor opens under the suggested values, not over them. */}
      <PopoverAnchor asChild>
        <div ref={cellRef} className="inline-flex flex-col items-center gap-1 align-middle">
          {box}
          {suggestedBid != null ? (
            <div className="flex items-center gap-1 whitespace-nowrap text-[10px] leading-tight">
              {suggestionLink(suggestedBid, 'recommended')}
              {suggestedMin != null && suggestedMax != null ? (
                <>
                  <span aria-hidden className="text-foreground-muted">
                    ·
                  </span>
                  {suggestionLink(suggestedMin, 'minimum')}
                  <span aria-hidden className="text-foreground-muted">
                    –
                  </span>
                  {suggestionLink(suggestedMax, 'maximum')}
                </>
              ) : null}
            </div>
          ) : null}
        </div>
      </PopoverAnchor>
      {editorContent}
    </Popover>
  );
}

function validBid(value: number | null | undefined): number | null {
  return typeof value === 'number' && Number.isFinite(value) && value > 0 ? value : null;
}
